import { Skeleton } from '@heroui/react';
import { Mail, Phone } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface AccountManagerSectionProps {
  name?: string;
  avatarUrl: string;
  email: string;
  phone: string;
  isLoading: boolean;
}

/**
 * Kompaktowa sekcja "Opiekun konta" — jedna pozioma linia:
 *   [avatar] [imię + rola] [email button] [phone button]
 *
 * Mieści się w 56px wysokości, łączna sekcja ~88px (z headerem).
 * Styl spójny z resztą /settings (border-radius 10px, var theme paddings).
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

const Avatar = styled.div<{ $bg?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $bg }) => ($bg ? `url(${$bg}) center/cover no-repeat` : 'linear-gradient(135deg, #1a4d4d, #0f3333)')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 400;
  text-transform: uppercase;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 1px;
`;

const Name = styled.div`
  font-size: 13.5px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Role = styled.div`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  text-decoration: none;

  svg { width: 14px; height: 14px; }

  &:hover {
    border-color: #1a4d4d;
    color: #1a4d4d;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(26,77,77,0.15)' : '#f0fdfa')};
  }

  &:focus-visible {
    outline: 2px solid rgba(26, 77, 77, 0.4);
    outline-offset: 1px;
  }
`;

export const AccountManagerSection: FC<AccountManagerSectionProps> = ({
  name,
  avatarUrl,
  email,
  phone,
  isLoading,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>{t('settings.accountManager', 'Opiekun konta')}</Title>
        </Header>
        <Card>
          <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
          <Info>
            <Skeleton className="h-3 w-32 rounded-md mb-1" />
            <Skeleton className="h-2.5 w-24 rounded-md" />
          </Info>
          <Actions>
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </Actions>
        </Card>
      </Container>
    );
  }

  const initial = (name || '').trim().charAt(0).toUpperCase() || '?';
  const showName = name && name !== 'undefined';

  return (
    <Container>
      <Header>
        <Title>{t('settings.accountManager', 'Opiekun konta')}</Title>
      </Header>
      <Card>
        <Avatar $bg={avatarUrl || undefined}>{!avatarUrl ? initial : null}</Avatar>
        <Info>
          {showName && <Name>{name}</Name>}
          <Role>{t('settings.accountManagerRole', 'Opiekun konta HotelSpot')}</Role>
        </Info>
        <Actions>
          {email && (
            <ActionButton
              href={`mailto:${email}`}
              title={email}
              aria-label={`${t('settings.email', 'E-mail')}: ${email}`}
            >
              <Mail />
            </ActionButton>
          )}
          {phone && (
            <ActionButton
              href={`tel:${phone}`}
              title={phone}
              aria-label={`${t('settings.phone', 'Telefon')}: ${phone}`}
            >
              <Phone />
            </ActionButton>
          )}
        </Actions>
      </Card>
    </Container>
  );
};
